import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { glide } from '../kit/motion';

export interface TabDef {
  id: string;
  label: ReactNode;
  hidden?: boolean;
}

/** Active tab from the URL (`?tab=`), falling back to the first visible tab. */
export function useTab(tabs: TabDef[], param = 'tab'): string {
  const [params] = useSearchParams();
  const visible = tabs.filter((t) => !t.hidden);
  const requested = params.get(param);
  return visible.find((t) => t.id === requested)?.id ?? visible[0]?.id ?? '';
}

/** URL-synced tab strip (UI kit segmented tabs): a pill glides under the active tab; links keep other query parameters. */
export function TabBar({ tabs, active, param = 'tab', label }: { tabs: TabDef[]; active: string; param?: string; label: string }) {
  const [params] = useSearchParams();
  const scroller = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const glider = useRef<HTMLSpanElement>(null);
  const hrefFor = (id: string) => {
    const next = new URLSearchParams(params);
    next.set(param, id);
    return `?${next.toString()}`;
  };
  const visible = tabs.filter((t) => !t.hidden);

  const place = (instant: boolean) => {
    const target = list.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    glide(glider.current, target, { axis: 'x', instant });
    // Keep the active tab visible in a strip that scrolls sideways, without scrolling the page.
    const strip = scroller.current;
    if (target && strip) {
      const left = target.offsetLeft - strip.clientWidth / 2 + target.offsetWidth / 2;
      if (target.offsetLeft < strip.scrollLeft || target.offsetLeft + target.offsetWidth > strip.scrollLeft + strip.clientWidth) strip.scrollTo?.({ left, behavior: instant ? 'auto' : 'smooth' });
    }
  };
  const first = useRef(true);
  useLayoutEffect(() => {
    place(first.current);
    first.current = false;
  });
  useEffect(() => {
    const onResize = () => place(true);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div ref={scroller} className="-mx-1 overflow-x-auto px-1 pb-1 dr-chrome">
      <div ref={list} role="tablist" aria-label={label} className="relative inline-flex min-w-max gap-0.5 rounded-field border border-hairline bg-base-200/70 p-1">
        <span ref={glider} aria-hidden="true" className="seg-glider pointer-events-none absolute top-1 left-0 z-0 rounded-[calc(var(--radius-field)-0.25rem)] bg-base-100 opacity-0 dr-shadow-xs ring-1 ring-hairline" />
        {visible.map((t) => (
          <Link
            key={t.id}
            role="tab"
            aria-selected={t.id === active}
            className="dr-focus relative z-10 inline-flex h-8 items-center gap-1.5 rounded-[calc(var(--radius-field)-0.25rem)] px-3 text-sm font-medium whitespace-nowrap text-base-content/60 transition-colors duration-200 hover:text-base-content aria-selected:text-base-content"
            to={hrefFor(t.id)}
            replace
            preventScrollReset
          >
            {t.label}
          </Link>
        ))}
      </div>
    </div>
  );
}
